import { JSX } from 'react';

import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import { Modal } from 'components/Modal';
import { Select } from 'components/Select';
import { Input } from 'components/input/Input';
import { AdditionalReference, LoadStopTypes } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import { Grid } from './layout';

interface ReferenceNumberModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onOk: () => void;
  editingRefIndex: Maybe<number>;
  tempRefNumber: AdditionalReference;
  onTempRefChange: (field: keyof AdditionalReference, value: any) => void;
  // Pickup/consignee enums, or a free-form label for multi-stop intermediates (e.g. "Stop 2").
  stop: LoadStopTypes | string;
  qualifierOptions: { description: string; code: string }[];
}

function referenceNumberLocationLabel(stop: LoadStopTypes | string): string {
  if (stop === LoadStopTypes.Pickup || stop === 'pickup') {
    return 'Pickup';
  }
  if (stop === LoadStopTypes.Consignee || stop === 'consignee') {
    return 'Consignee';
  }
  return stop;
}

export function ReferenceNumberModal({
  qualifierOptions,
  isOpen,
  onCancel,
  onOk,
  editingRefIndex,
  tempRefNumber,
  onTempRefChange,
  stop,
}: ReferenceNumberModalProps): JSX.Element {
  // Check if required fields are filled in.
  const isFormValid =
    tempRefNumber.qualifier?.trim() && tempRefNumber.number?.trim();

  return (
    <Modal
      title={`${editingRefIndex !== null ? 'Edit' : 'Add'} Reference Number - ${referenceNumberLocationLabel(stop)} Location`}
      open={isOpen}
      onOk={onOk}
      onCancel={onCancel}
      okText={editingRefIndex !== null ? 'Update' : 'Add'}
      cancelText='Cancel'
      width='min(520px, calc(100vw - 2rem))'
      closable={{ 'aria-label': 'Close Modal' }}
      okButtonProps={{ disabled: !isFormValid }}
    >
      <div className='space-y-5'>
        {/* Qualifier Select */}
        <div className='relative'>
          <Label name='qualifier'>
            <span>
              Qualifier <span className='text-error-500'>*</span>
            </span>
          </Label>
          <Select
            showSearch
            className='mt-1 w-full rounded-md'
            placeholder='Select qualifier...'
            value={tempRefNumber.qualifier}
            onChange={(value) => onTempRefChange('qualifier', value)}
            options={qualifierOptions.map((option) => ({
              value: option.code,
              label: `${option.code} - ${option.description}`,
            }))}
          />
        </div>

        {/* Reference Number */}
        <div>
          <Label name='refNumber'>
            <span>
              Reference number <span className='text-error-500'>*</span>
            </span>
          </Label>
          <Input
            placeholder='Enter reference number...'
            value={tempRefNumber.number}
            onChange={(e) => onTempRefChange('number', e.target.value)}
            className='mt-1 text-[14px] font-normal leading-[30px]'
          />
        </div>

        {/* Weight and Pieces Grid */}
        <Grid cols='2' gap='lg' className='mx-0 w-full'>
          <div>
            <Label name='weight'>Weight</Label>
            <Input
              type='number'
              placeholder='Enter weight...'
              step={0.01}
              value={tempRefNumber.weight}
              onChange={(e) =>
                onTempRefChange('weight', parseFloat(e.target.value) || 0)
              }
              className='mt-1 text-[14px] font-normal leading-[30px]'
            />
          </div>
          <div>
            <Label name='pieces'>Pieces</Label>
            <Input
              type='number'
              placeholder='Enter number of pieces...'
              step={1}
              value={tempRefNumber.pieces}
              onChange={(e) =>
                onTempRefChange('pieces', parseInt(e.target.value) || 0)
              }
              className='mt-1 text-[14px] font-normal leading-[30px]'
            />
          </div>
        </Grid>

        {/* Send to Driver Checkbox */}
        <div className='flex items-center space-x-2'>
          <Checkbox
            onCheckedChange={(checked) =>
              onTempRefChange('shouldSendToDriver', checked)
            }
            checked={tempRefNumber.shouldSendToDriver}
            label='Send to driver with load assignment'
            labelClassName='text-[14px] font-normal leading-none text-neutral-800 peer-disabled:cursor-not-allowed whitespace-normal! peer-disabled:opacity-70'
          />
        </div>
      </div>
    </Modal>
  );
}
